import { memo, useCallback, useMemo, useRef } from 'react';
import CustomInput from 'src/componentes/CustomInput';
import { useIntl } from 'react-intl';
import { Droppable, Draggable } from 'react-beautiful-dnd';
import {
  ArrowDown,
  ArrowUp,
  Copy,
  GripHorizontal,
  Image as ImageIcon,
  Trash2,
} from 'lucide-react';

import Button from 'src/componentes/button';
import ContentManager from 'src/componentes/content-manager';
import Confirm from 'src/componentes/message-box/confirm';
import SelectImage from 'src/componentes/arquivo-upload/select-image';
import ConfigApp from 'src/services/configApp';
import css from 'src/utils/css';
import createGuid from 'src/utils/guid';
import FormularioGlobalAddMenu from './FormularioGlobalAddMenu';
import FormularioGlobalBufferedInput from './FormularioGlobalBufferedInput';
import FormularioGlobalQuestionCard from './FormularioGlobalQuestionCard';
import FormularioGlobalResizableImage, {
  FORMULARIO_GLOBAL_IMAGE_SIZE,
  normalizeStoredImageSize,
} from './FormularioGlobalResizableImage';
import {
  createFormularioGlobalQuestion,
  cloneQuestionMatrixSettings,
  areQuestionMatrixSettingsEqual,
} from './FormularioGlobalQuestionTypes';

const classes = css`
  .section {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-left: 4px solid #0f7180;
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
    padding: 22px 26px;
  }

  .sectionInvalid {
    border-color: #dc3545;
    border-left-color: #dc3545;
  }

  .header {
    align-items: center;
    display: flex;
    gap: 12px;
    justify-content: space-between;
    margin-bottom: 18px;
  }

  .drag {
    display: flex;
    justify-content: center;
    margin-bottom: 8px;
  }

  .dragHandle {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: #94a3b8;
    cursor: move !important;
    display: inline-flex;
    height: 30px;
    justify-content: center;
    padding: 0;
    width: 30px;
  }

  .dragHandle:active {
    cursor: move !important;
  }

  .dragHandleDragging {
    cursor: move !important;
  }

  .dragHandle:hover {
    background: #f1f5f9;
    color: #0f7180;
  }

  .dragHandle:disabled {
    color: #cbd5e1;
    cursor: not-allowed;
  }

  .indexLabel {
    color: #64748b;
    font-size: 13px;
    font-weight: 700;
  }

  .actions {
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-end;
  }

  .iconButton {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 6px;
    color: #475569;
    display: inline-flex;
    height: 30px;
    justify-content: center;
    padding: 0;
    width: 30px;

    &:not(:disabled):hover {
      background: #f1f5f9;
      color: #0f7180;
    }

    &:disabled {
      color: #cbd5e1;
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  .required {
    align-items: center;
    border-left: 1px solid #e2e8f0;
    display: flex;
    gap: 10px;
    margin-left: 4px;
    padding-left: 14px;
  }

  .requiredLabel {
    color: #475569;
    font-size: 12px;
    margin-bottom: 0;
  }

  .description {
    margin-top: 10px;
  }

  .imageSettings {
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(2, minmax(120px, 180px));
    margin-top: 12px;
  }

  .footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 12px;
  }

  .questions {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-top: 18px;
  }

  .addQuestion {
    margin-top: 18px;
  }
`;

const getDraggableStyle = (draggableStyle, isDragging) => ({
  ...draggableStyle,
  opacity: isDragging ? 0.55 : 1,
});

const getQuestionDraggableId = (question, index, sectionDroppableId) =>
  `${sectionDroppableId}-question-${
    question._formularioGlobalClientId || question.id || index
  }`;

const getImageGuid = (imagem) => {
  if (!imagem) return null;
  if (typeof imagem === 'string') return imagem;

  return imagem.guid || imagem.imagemGuid || null;
};

const areQuestionOptionsEqual = (previousOptions = [], nextOptions = []) =>
  previousOptions.length === nextOptions.length &&
  previousOptions.every((previousOption, index) => {
    const nextOption = nextOptions[index];

    return (
      previousOption.id === nextOption?.id &&
      previousOption.texto === nextOption?.texto &&
      previousOption.titulo === nextOption?.titulo &&
      previousOption.valor === nextOption?.valor &&
      previousOption.destinoRamificacao === nextOption?.destinoRamificacao &&
      previousOption.destinoRamificacaoGuid ===
        nextOption?.destinoRamificacaoGuid &&
      previousOption.permitirComentario === nextOption?.permitirComentario &&
      previousOption.comentarioObrigatorio ===
        nextOption?.comentarioObrigatorio &&
      previousOption.ordem === nextOption?.ordem
    );
  });

const areQuestionsEqual = (previousQuestions = [], nextQuestions = []) =>
  previousQuestions.length === nextQuestions.length &&
  previousQuestions.every((previousQuestion, index) => {
    const nextQuestion = nextQuestions[index];

    return (
      previousQuestion.id === nextQuestion?.id &&
      previousQuestion.guid === nextQuestion?.guid &&
      previousQuestion.titulo === nextQuestion?.titulo &&
      previousQuestion.tipo === nextQuestion?.tipo &&
      previousQuestion.obrigatoria === nextQuestion?.obrigatoria &&
      previousQuestion.aplicarRamificacao ===
        nextQuestion?.aplicarRamificacao &&
      previousQuestion.permitirComentario ===
        nextQuestion?.permitirComentario &&
      previousQuestion.comentarioObrigatorio ===
        nextQuestion?.comentarioObrigatorio &&
      previousQuestion.arquivoSistemaId === nextQuestion?.arquivoSistemaId &&
      getImageGuid(previousQuestion.imagem) ===
        getImageGuid(nextQuestion?.imagem) &&
      previousQuestion.imagemLargura === nextQuestion?.imagemLargura &&
      previousQuestion.imagemAltura === nextQuestion?.imagemAltura &&
      previousQuestion.ordem === nextQuestion?.ordem &&
      areQuestionMatrixSettingsEqual(previousQuestion, nextQuestion || {}) &&
      areQuestionOptionsEqual(previousQuestion.opcoes, nextQuestion?.opcoes)
    );
  });

const areSectionsEqual = (previousSection = {}, nextSection = {}) =>
  previousSection.id === nextSection.id &&
  previousSection.titulo === nextSection.titulo &&
  previousSection.descricao === nextSection.descricao &&
  previousSection.obrigatoria === nextSection.obrigatoria &&
  previousSection.arquivoSistemaId === nextSection.arquivoSistemaId &&
  getImageGuid(previousSection.imagem) === getImageGuid(nextSection.imagem) &&
  previousSection.imagemLargura === nextSection.imagemLargura &&
  previousSection.imagemAltura === nextSection.imagemAltura &&
  previousSection.ordem === nextSection.ordem &&
  areQuestionsEqual(previousSection.perguntas, nextSection.perguntas);

const syncQuestionOrder = (perguntas = []) =>
  perguntas.map((pergunta, index) => ({
    ...pergunta,
    ordem: index + 1,
  }));

const cloneQuestion = (question, ordem) => ({
  ...question,
  _formularioGlobalClientId: undefined,
  guid: createGuid(),
  id: 0,
  opcoes: (question.opcoes || []).map((opcao) => ({ ...opcao, id: 0 })),
  ...cloneQuestionMatrixSettings(question),
  ordem,
});

const FormularioGlobalSection = ({
  branchingTargets,
  dragHandleProps,
  disabled,
  exibirConfiguracaoComentario = true,
  exibirConfiguracaoComentarioOpcao = true,
  isFirst,
  isDragging,
  isLast,
  numero,
  onChange,
  onClone,
  onMoveDown,
  onMoveUp,
  onRemove,
  section,
  randomizarPerguntas,
  sectionDroppableId,
  validationErrors,
}) => {
  const intl = useIntl();
  const imageGuid = getImageGuid(section.imagem);
  const imageUrl = useMemo(
    () =>
      imageGuid
        ? `${ConfigApp.ApiUrl}/ArquivoSistema/DownloadImagem?guid=${imageGuid}`
        : null,
    [imageGuid]
  );
  const imageSize = useMemo(
    () => normalizeStoredImageSize(section.imagemLargura, section.imagemAltura),
    [section.imagemAltura, section.imagemLargura]
  );
  const perguntas = useMemo(
    () => syncQuestionOrder(section.perguntas || []),
    [section.perguntas]
  );
  const sectionRef = useRef(section);
  const perguntasRef = useRef(perguntas);
  sectionRef.current = section;
  perguntasRef.current = perguntas;

  const handleChange = useCallback(
    (field, value) => {
      onChange({ ...sectionRef.current, [field]: value });
    },
    [onChange]
  );

  const handleChangeQuestions = useCallback(
    (nextQuestions) => {
      onChange({
        ...sectionRef.current,
        perguntas: syncQuestionOrder(nextQuestions),
      });
    },
    [onChange]
  );

  const handleResizeImage = useCallback(
    ({ width, height }) => {
      onChange({
        ...sectionRef.current,
        imagemLargura: width,
        imagemAltura: height,
      });
    },
    [onChange]
  );

  const handleAddQuestion = useCallback(
    (tipo) => {
      const currentQuestions = perguntasRef.current;

      handleChangeQuestions([
        ...currentQuestions,
        createFormularioGlobalQuestion(tipo, currentQuestions.length + 1, intl),
      ]);
    },
    [handleChangeQuestions, intl]
  );

  const handleChangeQuestion = useCallback(
    (index, question) => {
      const nextQuestions = [...perguntasRef.current];
      nextQuestions[index] = question;
      handleChangeQuestions(nextQuestions);
    },
    [handleChangeQuestions]
  );

  const handleCloneQuestion = useCallback(
    (index) => {
      const currentQuestions = perguntasRef.current;
      const question = currentQuestions[index];
      const nextQuestions = [...currentQuestions];
      nextQuestions.splice(index + 1, 0, cloneQuestion(question, index + 2));
      handleChangeQuestions(nextQuestions);
    },
    [handleChangeQuestions]
  );

  const handleRemoveQuestion = useCallback(
    (index) => {
      handleChangeQuestions(
        perguntasRef.current.filter((_, currentIndex) => currentIndex !== index)
      );
    },
    [handleChangeQuestions]
  );

  const handleMoveQuestion = useCallback(
    (index, direction) => {
      const currentQuestions = perguntasRef.current;
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= currentQuestions.length) return;

      const nextQuestions = [...currentQuestions];
      const current = nextQuestions[index];
      nextQuestions[index] = nextQuestions[targetIndex];
      nextQuestions[targetIndex] = current;

      handleChangeQuestions(nextQuestions);
    },
    [handleChangeQuestions]
  );

  const handleUploadImage = useCallback(() => {
    if (disabled) return;

    ContentManager.addContent(
      <SelectImage
        title={intl.formatMessage({
          id: 'labelAdicionarImagemSecao',
        })}
        model={{
          requestChange: (arquivo) => handleChange('imagem', arquivo),
        }}
      />
    );
  }, [disabled, handleChange, intl]);

  const handleRemove = useCallback(() => {
    ContentManager.addContent(
      <Confirm
        message={intl.formatMessage({
          id: 'labelDesejaMesmoExcluirEstaSecao',
        })}
        handleConfirm={onRemove}
      />
    );
  }, [intl, onRemove]);

  const requiredId = `form-section-required-${section.id || section.ordem}`;
  const hasSectionValidationErrors =
    !!validationErrors?.titulo ||
    Object.keys(validationErrors?.questions || {}).length > 0;

  return (
    <div
      className={`${classes.section} ${
        hasSectionValidationErrors ? classes.sectionInvalid : ''
      }`}
    >
      <div className={classes.drag}>
        <button
          disabled={disabled}
          type="button"
          {...dragHandleProps}
          className={`${classes.dragHandle} ${
            isDragging ? classes.dragHandleDragging : ''
          }`}
        >
          <GripHorizontal size={20} />
        </button>
      </div>
      <div className={classes.header}>
        <div className={classes.indexLabel}>
          {intl.formatMessage(
            {
              id: 'labelSecaoNumero',
            },
            {
              numero: numero ?? section.ordem,
            }
          )}
        </div>
        <div className={classes.actions}>
          <button
            className={classes.iconButton}
            disabled={disabled || isFirst}
            onClick={onMoveUp}
            title={intl.formatMessage({
              id: 'labelMoverCima',
            })}
            type="button"
          >
            <ArrowUp size={18} />
          </button>
          <button
            className={classes.iconButton}
            disabled={disabled || isLast}
            onClick={onMoveDown}
            title={intl.formatMessage({
              id: 'labelMoverBaixo',
            })}
            type="button"
          >
            <ArrowDown size={18} />
          </button>
          <button
            className={classes.iconButton}
            disabled={disabled}
            onClick={onClone}
            title={intl.formatMessage({
              id: 'labelClonar',
            })}
            type="button"
          >
            <Copy size={18} />
          </button>
          <button
            className={classes.iconButton}
            disabled={disabled}
            onClick={handleRemove}
            title={intl.formatMessage({
              id: 'excluir',
            })}
            type="button"
          >
            <Trash2 size={18} />
          </button>
          <button
            className={classes.iconButton}
            disabled={disabled}
            onClick={handleUploadImage}
            title={intl.formatMessage({
              id: 'labelAdicionarImagemSecao',
            })}
            type="button"
          >
            <ImageIcon size={18} />
          </button>
          <div className={classes.required}>
            <label className={classes.requiredLabel} htmlFor={requiredId}>
              {intl.formatMessage({
                id: 'obrigatoria',
              })}
            </label>
            <CustomInput
              checked={!!section.obrigatoria}
              disabled={disabled}
              id={requiredId}
              onChange={(event) =>
                handleChange('obrigatoria', event.target.checked)
              }
              type="switch"
            />
          </div>
        </div>
      </div>
      <FormularioGlobalBufferedInput
        commitOnChange
        disabled={disabled}
        onCommit={(value) => handleChange('titulo', value)}
        placeholder={intl.formatMessage({
          id: 'labelTituloSecao',
        })}
        value={section.titulo}
        validationMessage={
          validationErrors?.titulo
            ? intl.formatMessage({ id: validationErrors.titulo })
            : null
        }
      />
      <div className={classes.description}>
        <FormularioGlobalBufferedInput
          disabled={disabled}
          onCommit={(value) => handleChange('descricao', value)}
          placeholder={intl.formatMessage({
            id: 'labelDescricaoSecaoFormulario',
          })}
          rows={2}
          type="textarea"
          value={section.descricao}
        />
      </div>
      {imageUrl && (
        <FormularioGlobalResizableImage
          alt={section.titulo || intl.formatMessage({ id: 'labelImagemSecao' })}
          disabled={disabled}
          height={imageSize.height}
          onResize={handleResizeImage}
          src={imageUrl}
          width={imageSize.width}
        />
      )}
      {imageUrl && (
        <div className={classes.imageSettings}>
          <FormularioGlobalBufferedInput
            disabled={disabled}
            label={intl.formatMessage({
              id: 'labelLarguraImagem',
            })}
            max={FORMULARIO_GLOBAL_IMAGE_SIZE.MAX_WIDTH}
            min={FORMULARIO_GLOBAL_IMAGE_SIZE.MIN_WIDTH}
            onCommit={(value) =>
              handleChange(
                'imagemLargura',
                normalizeStoredImageSize(value, null).width
              )
            }
            type="number"
            value={imageSize.width || ''}
          />
          <FormularioGlobalBufferedInput
            disabled={disabled}
            label={intl.formatMessage({
              id: 'labelAlturaImagem',
            })}
            max={FORMULARIO_GLOBAL_IMAGE_SIZE.MAX_HEIGHT}
            min={FORMULARIO_GLOBAL_IMAGE_SIZE.MIN_HEIGHT}
            onCommit={(value) =>
              handleChange(
                'imagemAltura',
                normalizeStoredImageSize(null, value).height
              )
            }
            type="number"
            value={imageSize.height || ''}
          />
        </div>
      )}
      {imageUrl && !disabled && (
        <div className={classes.footer}>
          <Button
            color="secondary"
            leftIcon={Trash2}
            onClick={() => handleChange('imagem', null)}
            size="sm"
          >
            {intl.formatMessage({
              id: 'labelRemoverImagem',
            })}
          </Button>
        </div>
      )}
      {perguntas.length > 0 && (
        <Droppable
          droppableId={sectionDroppableId}
          isDropDisabled={disabled}
          type={sectionDroppableId}
        >
          {(provided) => (
            <div
              className={classes.questions}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {perguntas.map((question, index) => (
                <Draggable
                  draggableId={getQuestionDraggableId(
                    question,
                    index,
                    sectionDroppableId
                  )}
                  index={index}
                  isDragDisabled={disabled}
                  key={getQuestionDraggableId(
                    question,
                    index,
                    sectionDroppableId
                  )}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={getDraggableStyle(
                        provided.draggableProps.style,
                        snapshot.isDragging
                      )}
                    >
                      <FormularioGlobalQuestionCard
                        branchingTargets={branchingTargets}
                        randomizarPerguntas={randomizarPerguntas}
                        disabled={disabled}
                        dragHandleProps={provided.dragHandleProps}
                        isFirst={index === 0}
                        isDragging={snapshot.isDragging}
                        isLast={index === perguntas.length - 1}
                        onChange={(nextQuestion) =>
                          handleChangeQuestion(index, nextQuestion)
                        }
                        onClone={() => handleCloneQuestion(index)}
                        onMoveDown={() => handleMoveQuestion(index, 1)}
                        onMoveUp={() => handleMoveQuestion(index, -1)}
                        onRemove={() => handleRemoveQuestion(index)}
                        question={question}
                        exibirConfiguracaoComentario={
                          exibirConfiguracaoComentario
                        }
                        exibirConfiguracaoComentarioOpcao={
                          exibirConfiguracaoComentarioOpcao
                        }
                        scopeId={`section-${section.id || section.ordem}`}
                        validationErrors={validationErrors?.questions?.[index]}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      )}
      <div className={classes.addQuestion}>
        <FormularioGlobalAddMenu
          disabled={disabled}
          onAddQuestion={handleAddQuestion}
        />
      </div>
    </div>
  );
};

export default memo(
  FormularioGlobalSection,
  (previousProps, nextProps) =>
    previousProps.branchingTargets === nextProps.branchingTargets &&
    previousProps.randomizarPerguntas === nextProps.randomizarPerguntas &&
    previousProps.disabled === nextProps.disabled &&
    previousProps.exibirConfiguracaoComentario ===
      nextProps.exibirConfiguracaoComentario &&
    previousProps.exibirConfiguracaoComentarioOpcao ===
      nextProps.exibirConfiguracaoComentarioOpcao &&
    previousProps.isFirst === nextProps.isFirst &&
    previousProps.isDragging === nextProps.isDragging &&
    previousProps.isLast === nextProps.isLast &&
    previousProps.numero === nextProps.numero &&
    previousProps.sectionDroppableId === nextProps.sectionDroppableId &&
    previousProps.validationErrors === nextProps.validationErrors &&
    areSectionsEqual(previousProps.section, nextProps.section)
);
